
import { useState, useEffect } from "react";
import { getEnhancedCategories, saveCategory, archiveCategory, mergeCategories } from "@/utils/promptUtils";
import { useToast } from "@/hooks/use-toast";
import { Category, CategoryFormData } from "@/components/admin/types";

export function useCategoryManagement() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("name");
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const { toast } = useToast();
  
  // Load categories
  const loadCategories = async () => {
    setIsLoading(true);
    try {
      const data = await getEnhancedCategories();
      setCategories(data);
      setFilteredCategories(data);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast({
        title: "Error loading categories",
        description: "There was a problem loading categories.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Search and sort filtering
  useEffect(() => {
    if (!categories.length) return;
    
    // First filter by search query
    let filtered = [...categories];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cat => 
        cat.name.toLowerCase().includes(query) || 
        cat.slug.toLowerCase().includes(query) || 
        cat.description?.toLowerCase().includes(query) ||
        (cat.keywords && cat.keywords.some(kw => kw.toLowerCase().includes(query)))
      );
    }
    
    // Then sort the filtered results
    if (sortBy === "name") {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "prompts") {
      filtered.sort((a, b) => (b.promptCount || 0) - (a.promptCount || 0));
    } else if (sortBy === "newest") {
      filtered.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    }
    
    setFilteredCategories(filtered);
  }, [searchQuery, categories, sortBy]);
  
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  const handleSaveCategory = async (formData: CategoryFormData) => {
    if (!formData.name || !formData.slug) {
      toast({
        title: "Missing required fields",
        description: "Category name and slug are required.",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      const categoryId = await saveCategory(formData);
      
      if (!categoryId) {
        throw new Error("Failed to save category");
      }
      
      toast({
        title: "Category saved",
        description: `Category "${formData.name}" has been saved successfully.`,
      });
      
      // Reload categories
      await loadCategories();
      return true;
    } catch (error: any) {
      console.error("Error saving category:", error);
      toast({
        title: "Error saving category",
        description: error.message || "There was a problem saving the category.",
        variant: "destructive",
      });
      return false;
    }
  };
  
  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category);
    return {
      id: category.id,
      name: category.name,
      slug: category.slug,
      description: category.description || "",
      icon: category.icon || "",
      parent_id: category.parent_id,
      keywords: category.keywords || []
    };
  };
  
  const handleArchiveCategory = async (categoryId: string) => {
    try {
      const success = await archiveCategory(categoryId);
      
      if (!success) {
        throw new Error("Failed to archive category");
      }
      
      toast({
        title: "Category archived",
        description: "The category has been archived successfully.",
      });
      
      // Reload categories
      await loadCategories();
      return true;
    } catch (error: any) {
      console.error("Error archiving category:", error);
      toast({
        title: "Error archiving category",
        description: error.message || "There was a problem archiving the category.",
        variant: "destructive",
      });
      return false;
    }
  };
  
  const handleMergeCategories = async (mergeSource: string, mergeTarget: string) => {
    if (!mergeSource || !mergeTarget || mergeSource === mergeTarget) {
      toast({
        title: "Invalid selection",
        description: "Please select different source and target categories.",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      const success = await mergeCategories(mergeSource, mergeTarget);
      
      if (!success) {
        throw new Error("Failed to merge categories");
      }
      
      toast({
        title: "Categories merged",
        description: "The categories have been merged successfully.",
      });
      
      // Reload categories
      await loadCategories();
      return true;
    } catch (error: any) {
      console.error("Error merging categories:", error);
      toast({
        title: "Error merging categories",
        description: error.message || "There was a problem merging the categories.",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  return {
    categories,
    filteredCategories,
    isLoading,
    searchQuery,
    sortBy,
    setSortBy,
    selectedCategory,
    setSelectedCategory,
    handleSearch,
    handleSaveCategory,
    handleEditCategory,
    handleArchiveCategory,
    handleMergeCategories,
    loadCategories
  };
}
